/**
 * Logística → Documentos (`/logistica/documentos`).
 *
 * Puerto de `Logistics::documents_data` + `views/documents/index.php`: la
 * consulta de comprobantes emitidos con sus filtros (fechas, cliente y número),
 * el buscador libre y las mismas columnas del panel anterior.
 *
 * Las acciones de cada fila eran seis. Aquí están las **descargas** (el comprobante en
 * ticket de 80 mm o en A4 con el modelo de SUNAT, más XML y CDR, que son enlaces
 * directos) y se informa de las que todavía no se ofrecen, con su motivo:
 * - *Ver estado* y *Anular* ante SUNAT: dependen del servicio externo de CPE
 *   (`api_dir_empresa` + token), como los terminales dependen del POS.
 * - *Editar método de pago*: mueve el efectivo del turno, así que se habilita
 *   con la pantalla **Bóvedas** para no descuadrar el cuadre.
 * - *Nota de crédito* (cuando el comprobante ya salió del plazo de anulación): es
 *   la pantalla **Notas de Crédito** de esta misma categoría.
 *
 * La tabla se pagina en el servidor, porque hay más de 100.000 comprobantes.
 */

import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCode,
  FileText,
  Filter,
  Lock,
  RotateCcw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useBuscarClientes, useDocumentos } from '../documentos.api';
import type { Documento, DocumentosFiltros } from '../documentos.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Variante y etiqueta del comprobante, como los badges del panel anterior. */
function tipoBadge(tipo: Documento['tipo']): {
  variante: 'success' | 'brand' | 'neutral';
  etiqueta: string;
} {
  if (tipo === 'boleta') return { variante: 'success', etiqueta: 'Boleta' };
  if (tipo === 'factura') return { variante: 'brand', etiqueta: 'Factura' };

  return { variante: 'neutral', etiqueta: 'Pendiente' };
}

/** Color del estado de la API (SUNAT). */
function varianteEstadoApi(estado: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (estado === 'Aceptado') return 'success';
  if (estado === 'Anulado' || estado === 'Anulado por NC') return 'danger';
  if (estado === 'Rechazado') return 'danger';

  return 'warning';
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Botón de descarga, de PDF propio o de acción pendiente de una fila. */
function Accion({
  titulo,
  href,
  pendiente,
  cargando,
  onClick,
  icono: Icono,
}: {
  titulo: string;
  href?: string | null;
  pendiente?: string;
  cargando?: boolean;
  onClick?: () => void;
  icono: typeof Download;
}) {
  if (pendiente) {
    return (
      <Button variant="ghost" size="icon" title={`${titulo} · ${pendiente}`} disabled>
        <Lock className="size-4 text-ink-300" aria-hidden="true" />
      </Button>
    );
  }

  if (onClick) {
    return (
      <Button
        variant="ghost"
        size="icon"
        title={titulo}
        aria-label={titulo}
        loading={cargando}
        onClick={onClick}
      >
        <Icono className="size-4 text-ink-600" aria-hidden="true" />
      </Button>
    );
  }

  if (!href) {
    return (
      <Button variant="ghost" size="icon" title={`${titulo} · no disponible`} disabled>
        <Icono className="size-4 text-ink-300" aria-hidden="true" />
      </Button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={titulo}
      className="inline-flex size-10 items-center justify-center rounded-control text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
    >
      <Icono className="size-4" aria-hidden="true" />
    </a>
  );
}

export function DocumentosPage() {
  useDocumentTitle('Logística · Documentos');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [numero, setNumero] = useState('');
  const [cliente, setCliente] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<DocumentosFiltros>({ perPage: POR_PAGINA, page: 1 });
  const [sugerencias, setSugerencias] = useState('');
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const documentos = useDocumentos(filtros);
  const clientes = useBuscarClientes(sugerencias);

  const sinPermiso = documentos.error instanceof ApiError && documentos.error.status === 403;
  const lista = documentos.data?.documentos ?? [];
  const total = documentos.data?.total ?? 0;
  const pagina = documentos.data?.page ?? 1;
  const paginas = documentos.data?.paginas ?? 1;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica el filtro y vuelve a la primera página. */
  function aplicar(extra: Partial<DocumentosFiltros> = {}) {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      numero: numero.trim() || undefined,
      cliente: cliente.trim() || undefined,
      search: busqueda.trim() || undefined,
      perPage: POR_PAGINA,
      page: 1,
      ...extra,
    });
  }

  /** Abre el comprobante en ticket de 80 mm (lo genera la propia API). */
  async function imprimirTicket(documento: Documento) {
    const tipo = documento.tipo === 'factura' ? 'factura' : 'boleta';

    setErrorImpresion(null);
    setImprimiendo(documento.venta_id);

    try {
      await abrirPdfAutenticado(
        `/api/logistica/documentos/${documento.venta_id}/ticket?tipo=${tipo}`,
      );
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el comprobante.'));
    } finally {
      setImprimiendo(null);
    }
  }

  /** Abre el comprobante en A4, con el modelo de SUNAT. */
  async function imprimirA4(documento: Documento) {
    const tipo = documento.tipo === 'factura' ? 'factura' : 'boleta';

    setErrorImpresion(null);
    setImprimiendo(documento.venta_id);

    try {
      await abrirPdfAutenticado(
        `/api/logistica/documentos/${documento.venta_id}/ticket?tipo=${tipo}&formato=a4`,
      );
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el comprobante en A4.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <FileText className="size-6 text-brand-500" aria-hidden="true" />
          Documentos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Comprobantes emitidos (boletas y facturas) con su forma de pago, su estado ante SUNAT y sus
          descargas.
        </p>
      </div>

      {documentos.data ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{formatNumber(total)} comprobantes</Badge>
          <Badge variant="neutral">anulación hasta {documentos.data.dias_maximos_anular} días</Badge>
        </div>
      ) : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>logistics</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        Las descargas del comprobante en <strong>ticket de 80 mm</strong> y en{' '}
        <strong>A4 con el modelo de SUNAT</strong> (las dos con su código QR), más el{' '}
        <strong>XML y el CDR</strong>, ya están disponibles. El{' '}
        <strong>estado del comprobante</strong> y la <strong>anulación</strong> ante SUNAT dependen
        del servicio externo de CPE (como los terminales del servicio de POS) y la{' '}
        <strong>edición del método de pago</strong> mueve el efectivo del turno, así que se habilita
        junto con la pantalla <strong>Bóvedas</strong>. Las filas que lo requieran se marcan con un
        candado.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="doc-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="doc-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-cliente" className="text-sm font-medium text-ink-700">
                Cliente
              </label>
              <input
                id="doc-cliente"
                list="doc-clientes"
                className={cn(CONTROL, 'w-64')}
                placeholder="Nombre o documento"
                value={cliente}
                onChange={(evento) => {
                  setCliente(evento.target.value);
                  setSugerencias(evento.target.value);
                }}
              />
              <datalist id="doc-clientes">
                {(clientes.data?.clientes ?? []).map((item) => (
                  <option key={item.numero_doc} value={item.numero_doc}>
                    {item.nombre}
                  </option>
                ))}
              </datalist>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-numero" className="text-sm font-medium text-ink-700">
                Número
              </label>
              <input
                id="doc-numero"
                className={cn(CONTROL, 'w-32')}
                placeholder="00100334"
                value={numero}
                onChange={(evento) => setNumero(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="doc-buscar"
                className={cn(CONTROL, 'w-56')}
                placeholder="Cliente, placa, serie…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={() => aplicar()}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setNumero('');
                setCliente('');
                setBusqueda('');
                setFiltros({ perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el comprobante">
              {errorImpresion}
            </Alert>
          ) : null}

          {documentos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : documentos.error ? (
            <Alert variant="error" title="No se pudieron leer los documentos">
              {mensajeDe(documentos.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay comprobantes con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Documento</th>
                    <th className="py-2">Grifero</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2">M. Pago</th>
                    <th className="py-2 text-right">Monto</th>
                    <th className="py-2 text-right">Descuento</th>
                    <th className="py-2">Placa</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((documento) => {
                    const badge = tipoBadge(documento.tipo);

                    return (
                      <tr key={`${documento.venta_id}-${documento.id_documento}`} className="border-b border-ink-50 last:border-0">
                        <td className="py-3 text-ink-700">
                          <span className="block">{documento.fecha_emision ?? '—'}</span>
                          <span className="block text-[11px] text-ink-400">
                            {documento.creado ?? ''}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="block font-semibold text-ink-900">
                            {documento.cliente || '—'}
                          </span>
                          <span className="block text-[11px] text-ink-400">
                            {documento.documento_cliente || '—'}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="font-mono text-xs text-ink-700">
                            {documento.documento}
                          </span>
                        </td>
                        <td className="py-3 text-ink-600">
                          {documento.grifero || <span className="text-ink-300">—</span>}
                        </td>
                        <td className="py-3">
                          <Badge variant={badge.variante}>{badge.etiqueta}</Badge>
                          <span className="mt-1 block">
                            <Badge variant={varianteEstadoApi(documento.estado_api)}>
                              {documento.estado_etiqueta}
                            </Badge>
                          </span>
                        </td>
                        <td className="py-3 text-ink-600">
                          {documento.monto_pago_primario > 0 ? (
                            <span className="block text-xs">
                              {documento.metodo_pago.toUpperCase()} ·{' '}
                              {formatCurrency(documento.monto_pago_primario)}
                            </span>
                          ) : null}
                          {documento.monto_pago_secundario > 0 ? (
                            <span className="block text-xs">
                              {documento.metodo_pago_secundario.toUpperCase()} ·{' '}
                              {formatCurrency(documento.monto_pago_secundario)}
                            </span>
                          ) : null}
                          {documento.monto_pago_primario === 0 &&
                          documento.monto_pago_secundario === 0 ? (
                            <span className="text-ink-300">—</span>
                          ) : null}
                        </td>
                        <td className="py-3 text-right font-semibold text-ink-900">
                          {formatCurrency(documento.monto)}
                        </td>
                        <td className="py-3 text-right text-ink-600">
                          {formatCurrency(documento.descuento)}
                        </td>
                        <td className="py-3 text-ink-600">
                          {documento.placa || <span className="text-ink-300">—</span>}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-0.5">
                            <Accion
                              titulo="Ver ticket (80 mm)"
                              cargando={imprimiendo === documento.venta_id}
                              onClick={() => void imprimirTicket(documento)}
                              icono={FileText}
                            />
                            <Accion
                              titulo="Ver en A4 (modelo SUNAT)"
                              cargando={imprimiendo === documento.venta_id}
                              onClick={() => void imprimirA4(documento)}
                              icono={FileText}
                            />
                            <Accion
                              titulo="Descargar XML"
                              href={documento.xml_link || null}
                              icono={FileCode}
                            />
                            <Accion
                              titulo="Descargar CDR"
                              href={documento.cdr_link || null}
                              icono={Download}
                            />
                            <Accion
                              titulo="Editar método de pago"
                              pendiente="se habilita con Bóvedas"
                              icono={Download}
                            />
                            {documento.puede_anular ? (
                              <Accion
                                titulo="Anular comprobante"
                                pendiente="depende del servicio de CPE"
                                icono={Download}
                              />
                            ) : null}
                            {documento.puede_nota_credito ? (
                              <Accion
                                titulo="Generar nota de crédito"
                                pendiente="pantalla Notas de Crédito"
                                icono={Download}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} comprobantes · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}

          {documentos.data && !documentos.data.puede_editar_pago ? (
            <Alert variant="info">
              <span className={ICONO_EN_ALERTA}>
                <Search className="size-3.5" aria-hidden="true" />
                Tu usuario no tiene el permiso <strong>liquidaciones.metodos_pago.editar</strong>, así
                que no podrá editar formas de pago cuando se habiliten.
              </span>
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
